import { memo } from 'react';
import { useReduxState } from 'hooks';
import { AIGenerateChapterTitleTranslation } from 'components/aiTools';
import { Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  EditorBlockTranslationReference,
  useEditorContext
} from 'components/editor';
import { TitleEditor } from 'components/editor/inputs';
import { editorSocket } from 'components/editor/socket';

import type { Chapter } from 'shared-types';

interface Props {
  title: Chapter['title'];
  titleLocales: Chapter['titleLocales'];
}

const EditorTitle = ({ title, titleLocales }: Props) => {
  const {
    editorActiveLanguageVersion,
    editorChapterTitleIsEdited,
    editorTranslationReferenceLanguage,
    editorCurrentChapter,
    editorPreviewType
  } = useEditorContext();

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const handleChange = (updatedTitle: string) => {
    const isEdititingDefaultLanguage =
      !editorActiveLanguageVersion ||
      editorActiveLanguageVersion === course.language;

    editorSocket.updateTitle({
      chapterId: editorCurrentChapter.id,
      title: isEdititingDefaultLanguage ? updatedTitle : title,
      titleLocales: isEdititingDefaultLanguage
        ? titleLocales
        : { ...titleLocales, [editorActiveLanguageVersion]: updatedTitle }
    });
  };

  const isEdited = editorChapterTitleIsEdited && !editorPreviewType;

  return (
    <div className="title">
      {isEdited ? (
        <Tooltip childrenWrapInDiv={false} content={messages.commonEdited}>
          <div className="status-icon">
            <Icon icon="pen" />
          </div>
        </Tooltip>
      ) : null}

      <div className="title-editor">
        {isEdited ? <div className="status-bar" /> : null}

        <EditorBlockTranslationReference
          referenceHtml={
            editorActiveLanguageVersion
              ? titleLocales?.[editorTranslationReferenceLanguage] || title
              : ''
          }
          translationHtml={titleLocales?.[editorActiveLanguageVersion] || title}
        >
          <TitleEditor
            onChange={handleChange}
            placeholder={messages.editorTitlePlaceholder}
            readOnly={Boolean(editorPreviewType)}
            text={titleLocales?.[editorActiveLanguageVersion] || title}
            theme={theme}
            titleNumber={editorCurrentChapter.number}
          />
        </EditorBlockTranslationReference>

        {!editorPreviewType && (
          <div className="title-translate-action">
            <AIGenerateChapterTitleTranslation />
          </div>
        )}
      </div>

      <style jsx>{`
        .status-bar {
          background-color: ${theme.warningLightColor};
          border-radius: 2px;
          position: absolute;
          top: 0;
          bottom: 0;
          // aligns with the status bars of the blocks
          left: -9px;
          width: 4px;
        }
        .status-icon {
          align-items: center;
          background-color: ${theme.warningLightColor};
          border-radius: 20px;
          color: ${theme.warningLightTextColor};
          display: inline-flex;
          font-size: 0.9rem;
          justify-content: center;
          position: absolute;
          left: 2px;
          width: 20px;
          height: 20px;
        }
        .title {
          position: relative;
        }
        .title-editor {
          border: 1px solid transparent;
          border-radius: ${theme.inputBorderRadius};
          cursor: text;
          margin: 25px 40px 15px;
          padding: 5px 10px;
          position: relative;
        }
        .title-editor:hover,
        .title-editor:focus-within {
          border-color: #b1b8bf;
        }
        .title-translate-action {
          display: flex;
          justify-content: flex-end;
          margin-top: 8px;
          visibility: hidden;
        }
        .title-editor:hover .title-translate-action,
        .title-editor:focus-within .title-translate-action {
          visibility: visible;
        }
      `}</style>
    </div>
  );
};

export default memo(EditorTitle);
